<script setup lang="ts">
import { eventBus } from "#bb/lib/eventBus";
import { buildTemplateContext, useTemplating } from "#bb/composables/useTemplating";
import {
  formatTimerDuration,
  normalizeTimerDuration,
  type TimerInitialStatus,
  type TimerMode,
  type TimerPersistenceMode,
} from "#bb/lib/timer";
import { ConditionalRuleEvaluationKey } from "#bb/lib/conditionalRuleState";
import { cn } from "@/lib/utils";

interface FormContext {
  validateAll: () => Promise<boolean>;
}

const props = defineProps<{ id: string }>();

const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id) || {});

const features = inject("render-features", { editor: false });

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Timer editor",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the timer settings",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Timer",
    icon: "lucide:timer",
  },
});

const logger = useLogger("Timer");
const report = inject<unknown | null>("report", null);
const templateContext = computed(() => buildTemplateContext(report));
const { applyTemplate } = useTemplating(templateContext, { logger });
const label = computed(() =>
  applyTemplate(
    typeof definitionData.value.label === "string"
      ? definitionData.value.label
      : "Time remaining",
  ),
);

const durationMs = computed(() =>
  normalizeTimerDuration(
    (Number(definitionData.value.durationHours) || 0) * 3_600_000 +
      (Number(definitionData.value.durationMinutes) || 0) * 60_000 +
      (Number(definitionData.value.durationSeconds) || 0) * 1000,
  ),
);
const mode = computed<TimerMode>(() =>
  definitionData.value.mode === "count-up" ? "count-up" : "countdown",
);
const persistenceMode = computed<TimerPersistenceMode>(() =>
  definitionData.value.persistenceMode === "active-session"
    ? "active-session"
    : "wall-clock",
);
const initialStatus = computed<TimerInitialStatus>(() =>
  definitionData.value.initialStatus === "paused" ? "paused" : "running",
);
const {
  state,
  snapshot,
  initialized,
  lastError,
  shouldExecuteCompletion,
  claimCompletion,
  markCompletionExecuted,
  releaseCompletionClaim,
  pause,
  resume,
} = usePersistentTimer({
  id: () => props.id,
  mode,
  durationMs,
  persistenceMode,
  initialStatus,
  editor: () => features.editor,
});

const formContext = inject<FormContext | null>("form-context", null);
const actionError = shallowRef<unknown>(null);
const completionAttempted = shallowRef(false);
let completionMarked = false;
const conditionalRuleEvaluation = inject(ConditionalRuleEvaluationKey, null);
const controlAction = computed(
  () => conditionalRuleEvaluation?.value.controlAction ?? null,
);

const { busy, execute: executeCompletionAction } = useBlockCompletionAction({
  editor: () => features.editor,
  automationPath: () => definitionData.value.automationPath,
  navigationPath: () => definitionData.value.navigationPath,
  navigationDelaySeconds: () => definitionData.value.redirectDelaySeconds,
  beforeExecute: async () => {
    if (definitionData.value.validateForm === true && formContext) {
      return formContext.validateAll();
    }
    return true;
  },
  beforeNavigation: async () => {
    await markCompletionExecuted();
    completionMarked = true;
  },
});

watch(shouldExecuteCompletion, async (ready) => {
  if (!ready || completionAttempted.value) return;
  completionAttempted.value = true;
  actionError.value = null;

  try {
    if (!(await claimCompletion())) return;

    try {
      const executed = await executeCompletionAction();
      if (!executed) {
        await releaseCompletionClaim();
        return;
      }
    } catch (error) {
      await releaseCompletionClaim().catch(() => undefined);
      throw error;
    }

    if (!completionMarked) {
      await markCompletionExecuted();
      completionMarked = true;
    }
  } catch (error) {
    actionError.value = error;
    logger.error("Timer completion action failed", error);
  }
});

watch(
  [controlAction, initialized],
  async ([action, ready]) => {
    if (!ready || !action || features.editor) return;

    try {
      if (action === "stop" && state.value.status === "running") {
        await pause();
      } else if (action === "start" && state.value.status === "paused") {
        await resume();
      }
    } catch (error) {
      actionError.value = error;
      logger.error("Timer conditional action failed", error);
    }
  },
  { immediate: true },
);

const formattedTime = computed(() => formatTimerDuration(snapshot.value.displayMs));
const progressPercent = computed(() =>
  Math.min(100, Math.max(0, Math.round(snapshot.value.progress * 100))),
);
const displayMode = computed(() =>
  definitionData.value.displayMode === "progress" ? "progress" : "label",
);
const loading = computed(
  () => !features.editor && !initialized.value && !lastError.value,
);
const accessibleLabel = computed(() => {
  if (snapshot.value.completed) return `${label.value}: complete`;
  const direction = mode.value === "countdown" ? "remaining" : "elapsed";
  return `${label.value}: ${formattedTime.value} ${direction}`;
});
const errorMessage = computed(() => {
  const error = actionError.value || lastError.value;
  return error instanceof Error ? error.message : "The timer could not be loaded.";
});

useComponentConditionState(
  () => props.id,
  () =>
    initialized.value || features.editor
      ? {
          remainingSeconds: Math.floor(snapshot.value.remainingMs / 1000),
          elapsedSeconds: Math.floor(snapshot.value.elapsedMs / 1000),
          progressPercent: Math.round(snapshot.value.progress * 100),
          completed: snapshot.value.completed,
          status: state.value.status,
        }
      : {
          remainingSeconds: undefined,
          elapsedSeconds: undefined,
          progressPercent: undefined,
          completed: undefined,
          status: undefined,
        },
);
</script>

<template>
  <div
    :class="
      cn(
        'w-full rounded-lg bg-card text-card-foreground',
        $attrs.class ?? '',
      )
    "
  >
    <div
      v-if="loading"
      class="flex min-h-20 items-center justify-center gap-2 text-sm text-muted-foreground"
      role="status"
      aria-live="polite"
    >
      <Icon name="lucide:loader-circle" class="size-4 animate-spin" />
      <span>Loading timer…</span>
    </div>

    <div
      v-else-if="lastError && !initialized && !features.editor"
      class="flex min-h-20 items-center gap-3 text-sm text-destructive"
      role="alert"
    >
      <Icon name="lucide:triangle-alert" class="size-5 shrink-0" />
      <div>
        <p class="font-medium">Timer unavailable</p>
        <p class="mt-0.5 text-xs text-muted-foreground">{{ errorMessage }}</p>
      </div>
    </div>

    <div v-else class="space-y-3">
      <div class="flex items-end justify-between gap-4">
        <p class="text-sm font-medium">{{ label }}</p>
        <time
          class="font-mono text-2xl font-semibold tabular-nums tracking-tight"
          role="timer"
          :aria-label="accessibleLabel"
        >
          {{ formattedTime }}
        </time>
      </div>

      <Progress
        v-if="displayMode === 'progress'"
        :model-value="progressPercent"
        :aria-label="label"
        :aria-valuetext="accessibleLabel"
        class="h-2"
      />

      <p
        v-if="snapshot.completed"
        class="flex items-center gap-1.5 text-xs font-medium text-primary"
        role="status"
        aria-live="polite"
      >
        <Icon name="lucide:circle-check" class="size-3.5" />
        Complete
        <Icon
          v-if="busy"
          name="lucide:loader-circle"
          class="ml-auto size-3.5 animate-spin text-muted-foreground"
          aria-label="Running completion action"
        />
      </p>

      <p
        v-if="actionError"
        class="flex items-start gap-1.5 text-xs text-destructive"
        role="alert"
      >
        <Icon name="lucide:triangle-alert" class="mt-0.5 size-3.5 shrink-0" />
        <span>Completion action failed: {{ errorMessage }}</span>
      </p>

      <p
        v-else-if="lastError"
        class="flex items-start gap-1.5 text-xs text-warning"
        role="status"
      >
        <Icon name="lucide:cloud-alert" class="mt-0.5 size-3.5 shrink-0" />
        <span>Timer progress could not be saved. It will retry while this page remains open.</span>
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

export default {
  componentId: ComponentDefinitionIds.TIMER,
  editor: () => import("#bb/components/editor/content/timer.vue"),
  icon: "lucide:timer",
  type: "Timer",
  description:
    "A persistent count-up or countdown timer with optional completion actions.",
  default: {
    mode: "countdown",
    durationHours: 0,
    durationMinutes: 5,
    durationSeconds: 0,
    label: "Time remaining",
    displayMode: "progress",
    persistenceMode: "wall-clock",
    initialStatus: "running",
    validateForm: false,
    automationPath: "",
    navigationPath: "",
    redirectDelaySeconds: 0,
  },
  categories: ["Content"],
  order: 13023,
  conditionActions: ["start", "stop"],
  conditionStateFields: [
    {
      key: "remainingSeconds",
      label: "Remaining time",
      description: "Seconds remaining on the timer.",
      valueType: "duration",
      unit: "seconds",
    },
    {
      key: "elapsedSeconds",
      label: "Elapsed time",
      description: "Seconds elapsed on the timer.",
      valueType: "duration",
      unit: "seconds",
    },
    {
      key: "progressPercent",
      label: "Progress",
      description: "Timer progress from 0 to 100 percent.",
      valueType: "percentage",
      unit: "%",
    },
    {
      key: "completed",
      label: "Complete",
      valueType: "boolean",
    },
    {
      key: "status",
      label: "Status",
      valueType: "enum",
      options: [
        { label: "Running", value: "running" },
        { label: "Paused", value: "paused" },
        { label: "Completed", value: "completed" },
      ],
    },
  ],
};
</script>
